// src/pages/team/TeamApplicationsPage.tsx

import { useEffect, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { observer } from '@react';
import { useTeam, useAuth, useI18n } from '@react/hooks';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Alert,
  Badge,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  ErrorAlert,
} from '@react/components';
import {
  applicationStatusMap,
  getStatusMapping,
  getEntityId,
  getDisplayName,
  getInitials,
  getAvatarColor,
  formatRelativeTime,
  refEntity,
} from '@/features/team/logic';
import { TeamSectionNav } from '@/features/team/TeamSectionNav';

type ApplicationsTab = 'join' | 'role';
type StatusFilter = 'all' | 'APPLICATION_STATUS_PENDING' | 'APPLICATION_STATUS_APPROVED' | 'APPLICATION_STATUS_REJECTED';

export const TeamApplicationsPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const store = useTeam();
  const { user } = useAuth();
  const { t, locale } = useI18n();

  const [activeTab, setActiveTab] = useState<ApplicationsTab>('join');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [joinApps, setJoinApps] = useState<any[]>([]);
  const [roleApps, setRoleApps] = useState<any[]>([]);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [viewingApp, setViewingApp] = useState<any | null>(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [cancellingId, setCancellingId] = useState<string | null>(null);

  const loadJoinApps = useCallback(async () => {
    if (!id) return;
    try {
      const res = await store.ListTeamJoinApplications(id);
      setJoinApps(res?.applications?.applications ?? []);
    } catch { /* store handles */ }
  }, [id, store]);

  const loadRoleApps = useCallback(async () => {
    if (!id) return;
    try {
      const res = await store.ListTeamRoleApplications(id);
      setRoleApps(res?.applications?.applications ?? []);
    } catch { /* store handles */ }
  }, [id, store]);

  useEffect(() => {
    if (id) store.GetTeam(id);
    loadJoinApps();
    loadRoleApps();
  }, [id, loadJoinApps, loadRoleApps]);

  const showSuccess = (msg: string) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const handleApproveJoin = async (appId: string) => {
    if (!id) return;
    try {
      await store.ApproveTeamJoinApplication(id, appId, {
        application: refEntity(appId),
        team: refEntity(id),
        decision: 'approved',
      });
      showSuccess(t('team.applicationApproved'));
      loadJoinApps();
    } catch { /* store handles */ }
  };

  const handleApproveRole = async (appId: string) => {
    if (!id) return;
    try {
      await store.ApproveTeamRoleApplication(id, appId, {
        application: refEntity(appId),
        team: refEntity(id),
      });
      showSuccess(t('team.roleApplicationApproved'));
      loadRoleApps();
    } catch { /* store handles */ }
  };

  const handleView = async (app: any) => {
    const appId = getEntityId(app);
    if (!appId) return;
    setViewLoading(true);
    try {
      const res = activeTab === 'join'
        ? await store.GetTeamJoinApplication(appId)
        : await store.GetTeamRoleApplication(appId);
      setViewingApp(res?.application ?? app);
    } catch {
      setViewingApp(app);
    } finally {
      setViewLoading(false);
    }
  };

  const handleCancelApplication = async (app: any) => {
    const appId = getEntityId(app);
    if (!appId) return;
    try {
      if (activeTab === 'join') {
        await store.CancelTeamJoinApplication(appId, { application: { id: appId }, comment: '' } as never);
        showSuccess(t('team.applicationCancelled'));
        loadJoinApps();
      } else {
        await store.CancelTeamRoleApplication(appId, { application: { id: appId }, comment: '' } as never);
        showSuccess(t('team.roleApplicationCancelled'));
        loadRoleApps();
      }
    } catch { /* store handles */ } finally {
      setCancellingId(null);
    }
  };

  const isMyApplication = (app: any): boolean => {
    const myId = (user as any)?.entity?.id;
    return !!myId && app?.applicant?.entity?.id === myId;
  };

  const pendingJoinCount = joinApps.filter((a: any) => a.status === 'APPLICATION_STATUS_PENDING').length;
  const pendingRoleCount = roleApps.filter((a: any) => a.status === 'APPLICATION_STATUS_PENDING').length;

  const currentApps = activeTab === 'join' ? joinApps : roleApps;
  const filteredApps = statusFilter === 'all'
    ? currentApps
    : currentApps.filter((a: any) => a.status === statusFilter);

  const teamName = (store.currentItem as any)?.name || t('team.title');

  const TABS: { id: ApplicationsTab; label: string; count: number }[] = [
    { id: 'join', label: t('team.joinApplications'), count: pendingJoinCount },
    { id: 'role', label: t('team.roleApplications'), count: pendingRoleCount },
  ];

  const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
    { value: 'all', label: t('common.all') },
    { value: 'APPLICATION_STATUS_PENDING', label: t('team.appStatus.pending') },
    { value: 'APPLICATION_STATUS_APPROVED', label: t('team.appStatus.approved') },
    { value: 'APPLICATION_STATUS_REJECTED', label: t('team.appStatus.rejected') },
  ];

  const renderApplicant = (app: any) => {
    const name = getDisplayName(app.applicant);
    return (
      <div className="flex items-center gap-3">
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-semibold shrink-0"
          style={{ backgroundColor: getAvatarColor(name) }}
        >
          {getInitials(name)}
        </div>
        <div className="min-w-0">
          <div className="text-sm font-medium text-foreground truncate">{name}</div>
          {app.message && (
            <div className="text-xs text-muted-foreground truncate max-w-[200px]" title={app.message}>
              {app.message}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderStatus = (app: any) => {
    const sm = getStatusMapping(applicationStatusMap, app.status);
    return (
      <Badge variant={sm.variant as any} size="sm">
        {t(sm.labelKey)}
      </Badge>
    );
  };

  const renderReview = (app: any) => {
    const isPending = app.status === 'APPLICATION_STATUS_PENDING';
    if (isPending) {
      return <span className="text-xs text-muted-foreground">--</span>;
    }
    const reviewerName = getDisplayName(app.reviewed_by);
    return (
      <div className="min-w-0">
        <div className="text-sm text-foreground truncate">{reviewerName}</div>
        {app.reviewed_at && (
          <div className="text-xs text-muted-foreground">{formatRelativeTime(app.reviewed_at, locale)}</div>
        )}
        {app.review_notes && (
          <div className="text-xs text-muted-foreground italic truncate max-w-[160px]" title={app.review_notes}>
            {app.review_notes}
          </div>
        )}
      </div>
    );
  };

  const renderActions = (app: any, onApprove: (id: string) => void) => {
    const appId = getEntityId(app);
    const isPending = app.status === 'APPLICATION_STATUS_PENDING';
    const mine = isMyApplication(app);

    if (isPending && rejectingId === appId) {
      return (
        <div className="flex justify-end items-center gap-2">
          <span className="text-xs text-destructive whitespace-nowrap">{t('team.rejectConfirm')}</span>
          <Button size="sm" variant="danger" onClick={() => setRejectingId(null)}>
            {t('common.confirm')}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setRejectingId(null)}>
            {t('common.cancel')}
          </Button>
        </div>
      );
    }

    if (isPending && mine && cancellingId === appId) {
      return (
        <div className="flex justify-end items-center gap-2">
          <span className="text-xs text-destructive whitespace-nowrap">{t('team.cancelApplicationConfirm')}</span>
          <Button size="sm" variant="danger" onClick={() => handleCancelApplication(app)}>
            {t('common.confirm')}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setCancellingId(null)}>
            {t('common.cancel')}
          </Button>
        </div>
      );
    }

    return (
      <div className="flex justify-end gap-1">
        <Button size="sm" variant="ghost" onClick={() => handleView(app)}>
          {t('common.view')}
        </Button>
        {isPending && (
          <>
            <Button size="sm" onClick={() => onApprove(appId)}>
              {t('common.approve')}
            </Button>
            <Button variant="ghost" size="sm" className="text-destructive" onClick={() => setRejectingId(appId)}>
              {t('common.reject')}
            </Button>
          </>
        )}
        {isPending && mine && (
          <Button variant="ghost" size="sm" className="text-destructive" onClick={() => setCancellingId(appId)}>
            {t('team.cancelApplication')}
          </Button>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <nav className="text-sm">
        <Link to="/teams" className="text-muted-foreground hover:text-foreground">{t('team.list')}</Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to={`/teams/${id}`} className="text-muted-foreground hover:text-foreground">{teamName}</Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <span className="text-foreground">{t('team.nav.applications')}</span>
      </nav>
      <TeamSectionNav teamId={id ?? ''} group="people" active="applications" />


      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <h1 className="text-2xl font-bold text-foreground">{t('team.nav.applications')}</h1>
        {(pendingJoinCount + pendingRoleCount) > 0 && (
          <Badge variant="warning" size="sm">
            {pendingJoinCount + pendingRoleCount} {t('team.appStatus.pending').toLowerCase()}
          </Badge>
        )}
      </div>

      {store.error && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}
      {successMessage && <Alert variant="success" dismissible onDismiss={() => setSuccessMessage(null)}>{successMessage}</Alert>}

      <div className="border-b border-border">
        <div className="flex gap-0 -mb-px">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.id); setStatusFilter('all'); setRejectingId(null); }}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              }`}
            >
              {tab.label}
              {tab.count > 0 && (
                <Badge variant="warning" size="sm" className="ml-2">{tab.count}</Badge>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          {filteredApps.length === currentApps.length
            ? `${currentApps.length} ${activeTab === 'join' ? t('team.joinApplications').toLowerCase() : t('team.roleApplications').toLowerCase()}`
            : `${filteredApps.length} / ${currentApps.length}`
          }
        </p>
        <div className="flex gap-1">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => setStatusFilter(f.value)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                statusFilter === f.value
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:text-foreground'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <Card>
        <Table striped hoverable>
          <TableHeader>
            <TableRow>
              <TableHead>{t('common.applicant')}</TableHead>
              {activeTab === 'role' && <TableHead>{t('common.requestedRole')}</TableHead>}
              <TableHead>{t('common.status')}</TableHead>
              <TableHead>{t('common.date')}</TableHead>
              <TableHead>{t('common.reviewedBy')}</TableHead>
              <TableHead className="text-right">{t('common.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {store.isLoading ? (
              <TableRow>
                <TableCell colSpan={activeTab === 'role' ? 6 : 5} className="text-center py-8">
                  {t('common.loading')}
                </TableCell>
              </TableRow>
            ) : filteredApps.length === 0 ? (
              <TableEmpty
                colSpan={activeTab === 'role' ? 6 : 5}
                message={activeTab === 'join' ? t('team.noJoinApplications') : t('team.noRoleApplications')}
              />
            ) : (
              filteredApps.map((app: any) => {
                const appId = getEntityId(app);
                return (
                  <TableRow key={appId || Math.random()}>
                    <TableCell>{renderApplicant(app)}</TableCell>
                    {activeTab === 'role' && (
                      <TableCell>
                        <Badge variant="primary" size="sm">
                          {app.requested_role?.name || app.requested_role?.id || '--'}
                        </Badge>
                      </TableCell>
                    )}
                    <TableCell>{renderStatus(app)}</TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">
                        {formatRelativeTime(app.created_at, locale)}
                      </span>
                    </TableCell>
                    <TableCell>{renderReview(app)}</TableCell>
                    <TableCell className="text-right">
                      {renderActions(app, activeTab === 'join' ? handleApproveJoin : handleApproveRole)}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>

      {viewingApp && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          role="button"
          tabIndex={0}
          onClick={() => setViewingApp(null)}
          onKeyDown={(e) => { if (e.key === 'Escape' || e.key === 'Enter') setViewingApp(null); }}
        >
          <div className="absolute inset-0 bg-foreground/50 backdrop-blur-sm" />
          <Card className="relative w-full max-w-md" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            <CardHeader>
              <CardTitle>{t('team.applicationDetail')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {viewLoading ? (
                <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
              ) : (
                <dl className="divide-y divide-border">
                  <div className="py-2 grid grid-cols-3 gap-4">
                    <dt className="text-sm font-medium text-muted-foreground">{t('common.applicant')}</dt>
                    <dd className="text-sm text-foreground col-span-2">{getDisplayName(viewingApp.applicant)}</dd>
                  </div>
                  {activeTab === 'role' && (
                    <div className="py-2 grid grid-cols-3 gap-4">
                      <dt className="text-sm font-medium text-muted-foreground">{t('common.requestedRole')}</dt>
                      <dd className="text-sm text-foreground col-span-2">{viewingApp.requested_role?.name || viewingApp.requested_role?.id || '--'}</dd>
                    </div>
                  )}
                  <div className="py-2 grid grid-cols-3 gap-4">
                    <dt className="text-sm font-medium text-muted-foreground">{t('common.status')}</dt>
                    <dd className="text-sm text-foreground col-span-2">{renderStatus(viewingApp)}</dd>
                  </div>
                  {viewingApp.message && (
                    <div className="py-2 grid grid-cols-3 gap-4">
                      <dt className="text-sm font-medium text-muted-foreground">{t('common.message')}</dt>
                      <dd className="text-sm text-foreground col-span-2">{viewingApp.message}</dd>
                    </div>
                  )}
                  <div className="py-2 grid grid-cols-3 gap-4">
                    <dt className="text-sm font-medium text-muted-foreground">{t('common.date')}</dt>
                    <dd className="text-sm text-foreground col-span-2">{formatRelativeTime(viewingApp.created_at, locale)}</dd>
                  </div>
                  {viewingApp.review_notes && (
                    <div className="py-2 grid grid-cols-3 gap-4">
                      <dt className="text-sm font-medium text-muted-foreground">{t('common.reviewedBy')}</dt>
                      <dd className="text-sm text-foreground col-span-2">{viewingApp.review_notes}</dd>
                    </div>
                  )}
                </dl>
              )}
              <div className="flex justify-end pt-2">
                <Button variant="secondary" onClick={() => setViewingApp(null)}>{t('common.close')}</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
});

export default TeamApplicationsPage;
